import {
  ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MultiSelect, Option } from 'react-multi-select-component';
import styled, {
  createGlobalStyle,
  css,
  FlattenInterpolation,
  ThemeProps,
} from 'styled-components';
import { Input, InputCss } from './Input';
import { Flex } from '../../Flex';
import { ThemeType } from '../../../styling/Styles/ThemeType';
import { useIsXOverflow } from '../../../util';
import { useFormConfigurationContext } from '../../../..';

type TextFieldProps = {
  id: string,
  label?: string,
  value?: string[],
  onChange: (v: string[]) => void,
  options: { [key: string]: string },
  labelStyles?: string,
  disabled?: boolean,
  hasSelectAll?: boolean,
  disableSearch?: boolean,
  touchOverride?: boolean,
  placeholder?: string,
  onDeselectOption: (option: string) => void,
  selectedLabel?: string,
  valueRenderer?: (selected: Option[]) => ReactNode,
  arrowRenderer?: ({ expanded }: { expanded: boolean }) => any,
  required?: boolean,
  containerStyles?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  errors?: string[],
  warnings?: string[],
  onMenuClose?: (v: string[]) => void,
  isTooltipError?: boolean,
};

const GlobalMultiSelectStyle = createGlobalStyle<{ theme: ThemeType }>`
  .dropdown-container {
    display: flex;
    height: ${({ theme }) => theme.fontSize[9]};
    width: auto !important;
    border: 1px solid ${({ theme }) => theme.colours.neutral[2]};

    .dropdown-heading {
      height: auto !important;
      padding: ${({ theme }) => `${theme.padding[2]} ${theme.fontSize[2]}`};
    }
  }

  .rmsc {
    --rmsc-main: none;
  }

  .clear-selected-button {
    padding-left: 15px;
  }

  .multi-select-parent {
    background: none;
  }
    
  .rmsc .dropdown-content {
    z-index: 2;
  }

  .rmsc .select-item.selected {
    background-color: ${({ theme }) => theme.colours.neutral[10]};
  }

  .rmsc .select-item {
    &:hover {
      cursor: pointer;
      background-color: ${({ theme }) => theme.colours.secondary[6]};
    }
    &:active {
      font-weight: ${({ theme }) => theme.fontWeight.bold};
      background-color: ${({ theme }) => theme.colours.secondary[5]};
    }
  }

  .rmsc .item-renderer input {
    margin: auto 5px auto 0;
    accent-color: ${({ theme }) => theme.colours.primary[1]};
  }

  .rmsc .gray {
    color: ${({ theme }) => theme.textPrimary};
  }

`;

const StyledMultiSelect = styled(MultiSelect) <{ theme: ThemeType, hasError?: boolean, disabled?: boolean }>`
  .dropdown-container {
    border-color: ${({ theme, hasError }) => (hasError ? theme.textDanger : theme.colours.neutral[6])} !important;

    &:hover,:focus {
      box-shadow: none;
      border: ${({ theme }) => theme.borderWidth[1]} solid ${({ theme, hasError }) => (hasError ? theme.textDanger : theme.colours.primary[2])} !important;
    }

    &:focus-within {
      box-shadow: 0px 0px 0px ${({ theme }) => theme.padding[1]} ${({ theme, hasError }) => (hasError ? theme.danger : theme.colours.info[1])} !important;
    }

    background: ${({ theme, disabled }) => disabled && theme.colours.neutral[8]};
  }
  ${InputCss}
  padding: 0;
  border: none;
  min-width:250px;
`;

const MultiSelectContainer = styled(Flex)`
  max-width: 100%;
`;

const SelectedOptionClose = styled.i`${({ theme }) => `
  font-size:${theme.fontSize[5]};
`}`;

const SelectedOptionCloseAll = styled(SelectedOptionClose)`${({ theme }) => `
  color:${theme.colours.neutral[2]};
`}`;

const ExpandArrowContainer = styled.div`${({ theme }) => `
  margin-left:${theme.padding[3]};
`}`;

const ExpandArrow = styled.i.attrs(({ expanded }: { expanded: boolean }) => ({
  className: expanded ? 'mi-chevron-up' : 'mi-chevron-down',
})) <{ expanded: boolean }>`${({ theme }) => `
  font-size:${theme.fontSize[7]};
  color:${theme.colours.neutral[2]};
`}`;

const SelectedCount = styled.div.attrs({ className: 'selected-count' })`${({ theme }) => `
  margin-right: ${theme.padding[2]};
  margin-left: ${theme.padding[1]};
  border-radius: ${theme.borderRadius[1]};
  background: ${theme.colours.primary[1]};
  color: ${theme.colours.neutral[10]};
  width:22px;
  height:22px;
  text-align:center;
  font-size: ${theme.fontSize[2]};
  flex-shrink: 0;
`}`;

const SelectedContainer = styled(Flex).attrs({ className: 'selected-container' })`${({ theme }) => `
  gap: ${theme.padding[2]};
  align-items:center;
`}`;

const SelectedOptionContainer = styled(Flex)<{disabled?: boolean}>`${({ theme, disabled }) => `
  align-items:center;
  padding: ${theme.padding[1]};
  border-radius: ${theme.borderRadius[1]};
  background: ${disabled ? theme.colours.neutral[7] : theme.colours.primary[11]};
  color: ${theme.colours.primary[1]};
  font-weight: ${theme.fontWeight.bold};
  flex-shrink: 0;
`}`;

const SelectedOptionLabel = styled.div.attrs({ className: 'selected-option-label' })<{
  styles?: FlattenInterpolation<ThemeProps<ThemeType>> | string;
}>`
  ${({ theme }) => `
    padding-right: ${theme.padding[2]};
    max-width: ${theme.multiSelectChipLabelMaxWidth};
    overflow: hidden;
    text-overflow: ellipsis;
  `}
  ${({ styles }) => styles};
`;

const InfoText = styled.p<{
  styles?: FlattenInterpolation<ThemeProps<ThemeType>> | string;
}>`
  flex-shrink: 0;
  ${({ styles }) => styles};
`;

const InputStyles = css`
  padding:0; 
`.toString();

const MultiSelectInputWrapper = styled.div<{
  styles?: FlattenInterpolation<ThemeProps<ThemeType>> | string;
}>`
  width: 100%;
  ${({ styles }) => styles}
`;

const ListSelectedOptions = styled(Flex).attrs({ className: 'list-selected-options' })<{ hasSelected: boolean }>`${({ theme }) => `
  flex-grow: 1;
  overflow: hidden;
  gap: ${theme.padding[1]};
  align-items: center;
`}`;

const Placeholder = styled.div`${({ theme }) => `
  color: ${theme.colours.neutral[5]};
`}`;

const IconEllipse = styled.i`${({ theme }) => `
  font-size: ${theme.fontSize[4]};
  margin-top: ${theme.padding[2]};
`}`;

const defaultValueRenderer = (
  onDeselectOption: (option: string) => void,
  label: string,
  containerStyles?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  placeholder?: string,
) => function (selected: Option[]) {
  const ref = useRef<HTMLDivElement>(null);

  const isXOverflow = useIsXOverflow(ref, [selected]);
  const { isDisabled } = useFormConfigurationContext();

  return (
    <SelectedContainer>
      <ListSelectedOptions ref={ref} hasSelected={!!selected.length}>
        {label && <InfoText styles={containerStyles}>{label}</InfoText>}
        {selected.length ? Object.values(selected).map((v) => (
          <SelectedOptionContainer disabled={isDisabled} key={v.value} onClick={(e) => { e.stopPropagation(); }}>
            <SelectedOptionLabel styles={containerStyles}>
              {v.label}
            </SelectedOptionLabel>
            <SelectedOptionClose className="mi-close" onClick={() => !isDisabled && onDeselectOption(v.value ?? '')} />
          </SelectedOptionContainer>
        )) : (
          <Placeholder>{placeholder}</Placeholder>
        )}
      </ListSelectedOptions>
      {isXOverflow && <IconEllipse className="mi-options-horizontal" />}
      {!!selected.length && <SelectedCount>{selected.length}</SelectedCount>}
    </SelectedContainer>
  );
};

const defaultArrowRenderer = () => function ({ expanded }: { expanded: boolean; }) {
  return (
    <ExpandArrowContainer>
      <ExpandArrow expanded={expanded} />
    </ExpandArrowContainer>
  );
};

export function MultiSelectInput({
  id,
  label,
  value = [],
  onChange,
  options,
  labelStyles,
  disabled,
  touchOverride = false,
  hasSelectAll = true,
  disableSearch = false,
  onDeselectOption,
  selectedLabel = '',
  containerStyles,
  placeholder,
  valueRenderer = defaultValueRenderer(onDeselectOption, selectedLabel, containerStyles, placeholder),
  arrowRenderer = defaultArrowRenderer(),
  required = false,
  errors = [],
  warnings = [],
  onMenuClose = () => { },
  isTooltipError = false,
}: TextFieldProps) {
  const [touched, setTouched] = useState(false);

  const { isDisabled } = useFormConfigurationContext(disabled);

  const multiSelectOptions = useMemo(
    () => Object.entries(options).map(([key, optionValue]) => ({ label: optionValue, value: key })),
    [options],
  );

  const multiSelectValue = useMemo(
    () => value.map(
      (optionValue) => ({ label: options[optionValue], value: optionValue }),
    ),
    [value, options],
  );

  const onChangeMultiSelect = (newValue: { label: string, value: string }[]) => {
    onChange(newValue.map((optionValue) => (optionValue.value)));
  };

  const onMenuToggle = (expanded: boolean) => {
    setTouched(!expanded);
    if (!expanded) {
      onMenuClose(multiSelectValue.map((optionValue) => (optionValue.value)));
    }
  };

  const hasError = (touched || touchOverride) && !!errors?.length;

  return (
    <MultiSelectContainer>
      <MultiSelectInputWrapper styles={containerStyles}>
        <GlobalMultiSelectStyle />
        <Input
          id={id}
          label={label}
          hasError={hasError}
          errors={errors}
          warnings={warnings}
          required={required}
          labelStyles={labelStyles}
          containerStyles={InputStyles}
          isTooltipError={isTooltipError}
          hideErrorMessage={!touched}
        >
          <StyledMultiSelect
            className="multi-select-parent"
            options={multiSelectOptions}
            value={multiSelectValue}
            onChange={onChangeMultiSelect}
            labelledBy="Please select"
            disabled={isDisabled}
            hasSelectAll={hasSelectAll}
            disableSearch={disableSearch}
            valueRenderer={valueRenderer}
            onMenuToggle={onMenuToggle}
            ClearSelectedIcon={<SelectedOptionCloseAll className="mi-close" />}
            ArrowRenderer={arrowRenderer}
            hasError={hasError}
          />
        </Input>
      </MultiSelectInputWrapper>
    </MultiSelectContainer>
  );
}
